import React from "react";
import { BrowserRouter } from "react-router-dom";
import { Provider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import { ConfigProvider, App as AntdApp, theme } from "antd";
import { store, persistor } from "./store";
import ErrorBoundary from "./components/ErrorBoundary";
import PageLoading from "./components/PageLoading";
import NetworkStatus from "./components/NetworkStatus";
import { NotificationProvider } from "./components/NotificationProvider";
import AppRoutes from "./routes/AppRoutes";
import "@ant-design/v5-patch-for-react-19";
import "./styles/index.css";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "utils/queryClient";
import { useTheme } from "./hooks/useTheme";
import { useServerConnectivityCheck } from "./hooks/useServerConnectivity";

// Persist loading component
const PersistLoadingFallback = () => (
  <PageLoading tip="Loading application..." />
);

// Theme-aware App wrapper
const ThemedApp: React.FC = () => {
  const { isDark } = useTheme();

  // Start server connectivity monitoring
  useServerConnectivityCheck(30000, true);

  const antdTheme = {
    algorithm: isDark ? theme.darkAlgorithm : theme.defaultAlgorithm,
    token: {
      colorPrimary: "#1890ff",
      borderRadius: 6,
      fontFamily:
        '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif',
    },
    components: {
      Layout: {
        headerBg: isDark ? "#001529" : "#001529", // Keep header dark in both themes
        bodyBg: isDark ? "#141414" : "#f5f5f5",
        colorBgContainer: isDark ? "#1f1f1f" : "#ffffff",
      },
      Menu: {
        colorBgContainer: isDark ? "#001529" : "#001529", // Keep menu dark in both themes
        colorText: "#ffffff",
        colorTextSelected: "#ffffff",
        itemSelectedBg: "#1890ff",
        itemBg: "rgba(255, 255, 255, 0.1)",
      },
      Card: {
        colorBgContainer: isDark ? "#1f1f1f" : "#ffffff",
        colorBorderSecondary: isDark ? "#434343" : "#f0f0f0",
      },
      Table: {
        colorBgContainer: isDark ? "#1f1f1f" : "#ffffff",
        colorBorderSecondary: isDark ? "#434343" : "#f0f0f0",
      },
      Modal: {
        colorBgElevated: isDark ? "#1f1f1f" : "#ffffff",
      },
      Dropdown: {
        colorBgElevated: isDark ? "#1f1f1f" : "#ffffff",
      },
    },
  };

  return (
    <ConfigProvider theme={antdTheme}>
      <AntdApp>
        <NotificationProvider>
          <NetworkStatus />
          <ErrorBoundary>
            <BrowserRouter>
              <React.Suspense fallback={<PageLoading tip="Loading..." />}>
                <AppRoutes />
              </React.Suspense>
            </BrowserRouter>
          </ErrorBoundary>
        </NotificationProvider>
      </AntdApp>
    </ConfigProvider>
  );
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Provider store={store}>
        <PersistGate loading={<PersistLoadingFallback />} persistor={persistor}>
          <ThemedApp />
        </PersistGate>
      </Provider>
    </QueryClientProvider>
  );
}

export default App;
